/**
 * Content Filters Form Component
 *
 * Handles the content filtering section of the CreateAuditSessionDialog.
 * Extracted to reduce component complexity and improve maintainability.
 */

import type { SampleFilters } from '@internal-audit/domain';
import type { AuditSessionFilterOptions } from '@internal-audit/hooks/useAuditSessionFilterOptions';
import { Card, Heading, Section, Select, Text } from '@soteria/design-system-ve';

import * as styles from '../CreateAuditSessionDialog.css';
import { useContentFiltersForm } from '../hooks/useContentFiltersForm';

import { CustomValuesFilter } from './CustomValuesFilter';
import { FilterGroup } from './FilterGroup';

const POLICY_OPTIONS = [
  { label: 'All Content', value: 'all' },
  { label: 'Violates Policy', value: 'true' },
  { label: 'Does Not Violate Policy', value: 'false' },
];

function toSelectValue(violatesPolicy: boolean | undefined): string {
  if (violatesPolicy === true) return 'true';
  if (violatesPolicy === false) return 'false';

  return 'all';
}

function fromSelectValue(value: string): boolean | undefined {
  if (value === 'true') return true;
  if (value === 'false') return false;

  return undefined;
}

interface ContentFiltersFormProps {
  filters: SampleFilters;
  filterOptions: AuditSessionFilterOptions | null;
  filterOptionsLoading: boolean;
  totalLoading: boolean;
  discoveredCustomValues: Record<string, string[]>;
  discoveryLoading: boolean;
  onFiltersChange: (filters: SampleFilters) => void;
}

export function ContentFiltersForm({
  filters,
  filterOptions,
  filterOptionsLoading,
  totalLoading,
  discoveredCustomValues,
  discoveryLoading,
  onFiltersChange,
}: ContentFiltersFormProps) {
  const {
    handleLabelsChange,
    handleSeverityChange,
    handleTopicsChange,
    handleLanguagesChange,
    handleContentTypesChange,
    handleCustomValuesChange,
    handleViolatesPolicyChange,
    filterOptions: memoizedFilterOptions,
  } = useContentFiltersForm({ filters, filterOptions, onFiltersChange });

  return (
    <Section spacing="medium">
      <Card>
        <Section spacing="medium">
          <Heading level="h3">Content Filters</Heading>
          <Text>Configure filters to narrow down the content pool for auditing</Text>

          <div className={`${styles.filtersGrid} ${styles.formRow}`}>
            <FilterGroup
              disabled={totalLoading}
              error={filterOptions?.standardLabels.length === 0 ? 'No system labels available' : undefined}
              label="System Labels"
              loading={filterOptionsLoading}
              options={memoizedFilterOptions.labelOptions}
              placeholder="Select system labels..."
              selectedValues={filters.labels || []}
              onSelectionChange={handleLabelsChange}
            />

            <FilterGroup
              disabled={totalLoading}
              label="Severity Levels"
              loading={filterOptionsLoading}
              options={memoizedFilterOptions.severityOptions}
              placeholder="Select severity levels..."
              selectedValues={filters.labelSeverities?.map(s => s.toString()) || []}
              onSelectionChange={handleSeverityChange}
            />

            <FilterGroup
              disabled={totalLoading}
              label="Content Topics"
              loading={filterOptionsLoading}
              options={memoizedFilterOptions.topicOptions}
              placeholder="Select content topics..."
              selectedValues={filters.topics || []}
              onSelectionChange={handleTopicsChange}
            />

            <FilterGroup
              disabled={totalLoading}
              label="Content Languages"
              loading={filterOptionsLoading}
              options={memoizedFilterOptions.languageOptions}
              placeholder="Select languages..."
              selectedValues={filters.languages || []}
              onSelectionChange={handleLanguagesChange}
            />

            <FilterGroup
              disabled={totalLoading}
              label="Content Types"
              loading={filterOptionsLoading}
              options={memoizedFilterOptions.contentTypeOptions}
              placeholder="Select content types..."
              selectedValues={filters.contentTypes || []}
              onSelectionChange={handleContentTypesChange}
            />

            <div className={styles.filterGroup}>
              <Select
                disabled={totalLoading}
                label="Policy Violation"
                options={POLICY_OPTIONS}
                placeholder="All Content"
                value={toSelectValue(filters.violatesPolicy)}
                onChange={(selectedValue: string) => handleViolatesPolicyChange(fromSelectValue(selectedValue))}
              />
            </div>

            <CustomValuesFilter
              customValues={filters.customValues}
              disabled={totalLoading}
              discoveredCustomValues={discoveredCustomValues}
              loading={discoveryLoading}
              onCustomValuesChange={handleCustomValuesChange}
            />
          </div>
        </Section>
      </Card>
    </Section>
  );
}
